import { useEffect, useState } from "react";
import { ContactForm } from "./ContactForm";
import { Shield, Clock, Users, AlertCircle, Star } from "lucide-react";
import heroImage from "@/assets/hero-roofing.jpg";
import { supabase } from "@/integrations/supabase/client";

interface ServiceHeroProps {
  service: {
    name: string;
    hero_title_template: string;
    hero_subtitle_template: string;
    is_emergency: boolean;
  };
  cityName?: string;
}

export const ServiceHero = ({ service, cityName = "Long Island" }: ServiceHeroProps) => {
  const [county, setCounty] = useState<string>("Long Island");

  useEffect(() => {
    const fetchCounty = async () => {
      if (!cityName || cityName === "Long Island") {
        setCounty("Long Island");
        return;
      }

      try {
        const { data, error } = await supabase
          .from('city_groups')
          .select('county')
          .or(`primary_city.ilike.${cityName},display_cities.cs.{${cityName}}`)
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching county:', error);
          setCounty("Long Island");
        } else if (data?.county) {
          setCounty(data.county);
        } else {
          setCounty("Long Island");
        }
      } catch (error) {
        console.error('Error fetching county:', error);
        setCounty("Long Island");
      }
    };

    fetchCounty();
  }, [cityName]);

  // Handle title template replacement
  let heroTitle = service.hero_title_template
    .replace('{city}', cityName)
    .replace('{county}', county);
  
  // Fix duplicate "Long Island" issue (e.g., "Services in Long Island, Long Island")
  if (cityName === "Long Island") {
    heroTitle = heroTitle.replace(', Long Island', '');
  }

  const heroSubtitle = service.hero_subtitle_template
    ?.replace('{city}', cityName)
    ?.replace('{county}', county);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${heroImage})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/85 to-blue-700/90" />

      {/* Content */}
      <div className="relative container mx-auto px-4 py-12 lg:py-16 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Column - Hero Text */}
          <div className="text-white space-y-6">
            {service.is_emergency && (
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full animate-pulse">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">24/7 Emergency Service Available</span>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-xs font-medium text-blue-100 tracking-wide uppercase">
                Affordable. Reliable. Fully Insured.
              </p>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                {heroTitle}
              </h1>
              {heroSubtitle && (
                <p className="text-lg text-blue-100 leading-relaxed max-w-lg">
                  {heroSubtitle}
                </p>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">Licensed & Insured</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">Serving {county === "Long Island" ? county : `${county} County`} Since 2007</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">{service.is_emergency ? '24/7' : 'Expert'} Service</p>
                </div>
              </div>
            </div>

            {/* Hero Testimonial */}
            <div className="pt-4">
              <div className="bg-blue-800/30 border border-blue-600/30 rounded-lg p-6 backdrop-blur-sm">
                {/* 5 Star Rating */}
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                {/* Testimonial Quote */}
                <p className="text-blue-100 text-lg italic mb-3">
                  "Exceptional service and quality workmanship. They completed our {service.name.toLowerCase()} on time and within budget!"
                </p>
                <p className="text-blue-200 font-medium mb-4">
                  - Verified Customer, {cityName}
                </p>
                
                {/* Reviews Link */}
                <button
                  onClick={() => {
                    const testimonialsSection = document.getElementById('testimonials');
                    testimonialsSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-blue-200 hover:text-white font-medium underline underline-offset-4 hover:underline-offset-8 transition-all duration-300"
                >
                  View All Reviews →
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="flex justify-center lg:justify-end">
            <ContactForm cityName={cityName} serviceName={service.name} />
          </div>
        </div>
      </div>
    </section>
  );
};
